import type { ComponentProps, ReactNode } from 'react'

import Link from 'next/link'

import { cn } from '@/lib/utils'

/**
 * Сайтын ЖИШИГ товчны хэлбэр.
 *
 * Табны бөмбөлөгтэй нэг гэр бүл ([[tab-style]]) — захиалагчийн өгсөн утга.
 * Шинэ хэсэг нэмэх бүрд `ui/button`-ын өөр өөр `variant` сонгохын оронд
 * ҮРГЭЛЖ энэ компонентыг ашиглана.
 *
 * Энгийн үед ЦАГААН дэвсгэртэй, сонгогдсон үед ЦӨЦГИЙ (`#fbead8`) болно.
 *
 * ⚠️ Өнгө нь ТОГТМОЛ (`#dec7ae` хүрээ, хар бичиг) — theme-ийн
 * токен биш тул харанхуй горимд ч цайвар бөмбөлөг хэвээр байна. Захиалагчийн
 * шууд заасан утга.
 */
export const PILL_BUTTON = [
  'inline-flex items-center justify-center gap-2 rounded-full border px-5 py-2.5 text-xl font-medium whitespace-nowrap transition',
  'border-[#dec7ae] bg-white text-black',
  'dark:border-[#dec7ae] dark:bg-white dark:text-black',
  'hover:bg-[#fdf6f0] dark:hover:bg-[#fdf6f0]',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black',
  'disabled:pointer-events-none disabled:opacity-50'
].join(' ')

/**
 * Сонгогдсон төлөв — урвуу өнгө.
 *
 * Шүүлтүүрийн бөмбөлгүүд (ж: /brands-ийн үйлдвэрлэгч) таб шиг ажилладаг ч
 * `Tabs` компонент биш тул `data-active` атрибутгүй. Тиймээс төлөвийг
 * гаднаас өгнө.
 */
const PILL_ACTIVE = [
  'border-[#dec7ae] bg-[#fbead8] text-black hover:bg-[#fbead8]',
  'dark:border-[#dec7ae] dark:bg-[#fbead8] dark:text-black dark:hover:bg-[#fbead8]'
].join(' ')

type PillProps = {
  children: ReactNode
  className?: string

  /** Шүүлтүүрийн одоогийн сонголт эсэх */
  active?: boolean

  /**
   * Байвал холбоос, эс байвал товч болно.
   *
   * Гадаад хаяг (`http`-ээр эхэлсэн) нь шинэ табд нээгдэж, `next/link`-ийн
   * дотоод шилжилтийг ашиглахгүй — өөр домэйнд тэр нь ажиллахгүй.
   */
  href?: string
}

export const PillButton = ({
  children,
  className,
  href,
  active,
  ...rest
}: PillProps & Omit<ComponentProps<'button'>, 'className' | 'children'>) => {
  const classes = cn(PILL_BUTTON, active && PILL_ACTIVE, className)

  if (href) {
    const external = /^https?:\/\//.test(href)

    return external ? (
      <a href={href} target='_blank' rel='noopener noreferrer' className={classes}>
        {children}
      </a>
    ) : (
      <Link href={href} className={classes} aria-current={active ? 'page' : undefined}>
        {children}
      </Link>
    )
  }

  return (
    <button type='button' className={classes} aria-pressed={active} {...rest}>
      {children}
    </button>
  )
}
